import { React, useEffect, useState } from "react";
import "./Reservations.css";
import AppWrapper from "../AppWrapper";

import supabase from "../../supabase-client";
import { useBranch } from "../../context/BranchContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Page,
  Button,
  LegacyCard,
  IndexTable,
  Text,
  TextField,
  Filters,
  BlockStack,
  Spinner,
  Pagination,
  Badge,
  Popover,
  ChoiceList,
} from "@shopify/polaris";
import { SettingsIcon } from "@shopify/polaris-icons";
import { formatDate, RESERVATION_STATUS_ORDER } from "../../utility/funcs";
import * as XLSX from "xlsx/xlsx.mjs";
import {
  applyHeaderFormats,
  moneyHeaders,
  toExcelSerial,
  DATE_FMT,
} from "../../utility/xlsxExport";

const COLUMN_DEFS = [
  { key: "id", title: "ID", required: true },
  { key: "customer", title: "Cliente" },
  { key: "vendor", title: "Vendedor" },
  { key: "from", title: "Entrega" },
  { key: "to", title: "Devolución" },
  { key: "plate", title: "Placa" },
  { key: "model", title: "Modelo" },
  { key: "balance", title: "Saldo" },
  { key: "saldo_hoy", title: "Saldo a hoy" },
  { key: "status", title: "Estado" },
  { key: "invoice", title: "Facturación" },
];

const DEFAULT_COLUMNS = COLUMN_DEFS.map((c) => c.key);
// Bumped to v2 when the "Saldo a hoy" column was added, so existing users
// (whose stored v1 set predates it) pick up the new column by default.
const COLUMNS_STORAGE_KEY = "reservations_visible_columns_v2";

// Advanced filter choices (Polaris ChoiceList, multi-select). No "all" entry —
// an empty selection means no filter. Values map to the search_reservations RPC
// array params (p_balance / p_status / p_invoice).
const BALANCE_CHOICES = [
  { label: "Saldo pendiente", value: "pending" },
  { label: "Sin saldo", value: "none" },
  { label: "Saldo a favor", value: "credit" },
];
// Accrued-to-today saldo (floored at 0): "pending" = owes money for time
// already consumed, "none" = up to date (paid ahead or nothing due yet).
const SALDO_HOY_CHOICES = [
  { label: "Con saldo a hoy", value: "pending" },
  { label: "Sin saldo a hoy", value: "none" },
];
const INVOICE_CHOICES = [
  { label: "Sin factura", value: "none" },
  { label: "Parcialmente facturada", value: "partial" },
  { label: "Facturada", value: "full" },
];
// Ordered by the canonical reservation lifecycle (see funcs.js) so the filter
// reads in a sensible flow rather than an arbitrary order.
const STATUS_CHOICES = RESERVATION_STATUS_ORDER.map((s) => ({ label: s, value: s }));

function loadStoredColumns() {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_COLUMNS;
    // migrate legacy "car" key → "plate" + "model"
    const migrated = parsed.flatMap((key) =>
      key === "car" ? ["plate", "model"] : [key]
    );
    // keep only known keys, and preserve canonical order from COLUMN_DEFS
    const known = new Set(migrated);
    const ordered = COLUMN_DEFS
      .filter((c) => known.has(c.key) || c.required)
      .map((c) => c.key);
    return ordered.length ? ordered : DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
}

function Reservations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branch } = useBranch();
  const [reservations, setReservations] = useState([]);
  const [search, setSearch] = useState("");
  const page = parseInt(searchParams.get("page")) || 1;
  const setPage = (newPage) => {
    const params = new URLSearchParams(searchParams);
    if (newPage === 1) params.delete("page");
    else params.set("page", String(newPage));
    setSearchParams(params);
  };
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Advanced filters (multi-select, applied server-side via the RPC).
  const [vendorFilter, setVendorFilter] = useState([]);
  const [balanceFilter, setBalanceFilter] = useState([]);
  const [saldoHoyFilter, setSaldoHoyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState([]);
  const [vendorChoices, setVendorChoices] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(loadStoredColumns);
  const [columnsPopoverOpen, setColumnsPopoverOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const pageSize = 20;

  const handleColumnsChange = (selected) => {
    // always keep required columns
    const withRequired = COLUMN_DEFS
      .filter((c) => c.required || selected.includes(c.key))
      .map((c) => c.key);
    setVisibleColumns(withRequired);
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(withRequired));
    } catch {
      // ignore storage errors (quota, private mode, etc.)
    }
  };

  const fetchReservations = async () => {
    setLoading(true);

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabase.rpc("search_reservations", {
      search_text: search?.trim() || null,
      from_index: from,
      to_index: to,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      p_branch_id: branch?.id || null,
      p_vendor: vendorFilter.length ? vendorFilter : null,
      p_balance: balanceFilter.length ? balanceFilter : null,
      p_status: statusFilter.length ? statusFilter : null,
      p_invoice: invoiceFilter.length ? invoiceFilter : null,
      p_saldo_hoy: saldoHoyFilter.length ? saldoHoyFilter : null,
    });

    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }

    setReservations(data);
    setHasMore(data?.length >= pageSize);
    setLoading(false);
  };

  // Everything (search, date, and the four filters) applies automatically;
  // debounced so typing in the search field doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(fetchReservations, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    branch,
    search,
    dateFrom,
    dateTo,
    vendorFilter,
    balanceFilter,
    saldoHoyFilter,
    statusFilter,
    invoiceFilter,
  ]);

  // Load the distinct vendor list once for the Vendedor filter.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.rpc("get_reservation_vendors");
      if (data) {
        setVendorChoices(
          data.map((v) => ({ label: v.vendor_name, value: v.vendor_name }))
        );
      }
    })();
  }, []);

  const statusLabel = (res) => {
    if (res.status === "cancelled") return "Cancelada";
    if (res.status === "no_show") return "No-Show";
    if (res.status === "completed") return "Completada";
    if (res.status === "quotation") return "Cotización";
    if (res.checked_out && !res.checked_in) return "Entregado";
    if (res.checked_out && res.checked_in) return "Devuelto";
    if (res.fully_paid) return "Pagada";
    return "Reservado";
  };

  const statusBadge = (res) => {
    const label = statusLabel(res);
    if (res.status === "no_show") return <Badge tone="warning">{label}</Badge>;
    if (res.status === "quotation") return <Badge tone="info">{label}</Badge>;
    if (label === "Entregado" || label === "Pagada")
      return <Badge tone="success">{label}</Badge>;
    if (label === "Reservado")
      return <Badge tone="attention">{label}</Badge>;
    return <Badge>{label}</Badge>;
  };

  const typeBadge = (res) => {
    if (res.type === "service")
      return <Badge tone="warning">Servicio</Badge>;
    return null;
  };

  const parseDateRange = (dateRange) => {
    if (!dateRange) return { from: "-", to: "-" };
    try {
      // tstzrange format: ["2025-04-01 09:00","2025-04-05 09:00")
      const clean = dateRange.replace(/[\[\]()]/g, "");
      const parts = clean.split(",");
      const fromStr = parts[0]?.replace(/"/g, "").trim();
      const toStr = parts[1]?.replace(/"/g, "").trim();
      return {
        from: fromStr ? formatDate(fromStr.substring(0, 10)) + " " + fromStr.substring(11, 16) : "-",
        to: toStr ? formatDate(toStr.substring(0, 10)) + " " + toStr.substring(11, 16) : "-",
      };
    } catch {
      return { from: "-", to: "-" };
    }
  };

  // Like parseDateRange but returns real Date objects — the export turns them
  // into Excel serials (toExcelSerial), so the sheet gets true date cells
  // (sortable/filterable) instead of pre-formatted text.
  const parseDateRangeAsDates = (dateRange) => {
    if (!dateRange) return { from: null, to: null };
    try {
      const clean = dateRange.replace(/[\[\]()]/g, "");
      const parts = clean.split(",");
      const fromStr = parts[0]?.replace(/"/g, "").trim();
      const toStr = parts[1]?.replace(/"/g, "").trim();
      const toDate = (s) => {
        if (!s) return null;
        // Bounds arrive as "2026-05-16 09:00:00+00" — a bare two-digit "+00"
        // offset that new Date() rejects as Invalid Date (which is why exports
        // came out blank). Build a timezone-naive local date straight from the
        // wall-clock components so Excel shows the same day/time as the on-screen
        // table (which renders the raw digits); parsing via new Date() would also
        // shift the hours by the local timezone.
        const m = s.match(
          /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
        );
        if (!m) return null;
        const [, y, mo, d, h, mi, se] = m;
        const dt = new Date(+y, +mo - 1, +d, +h, +mi, +(se || 0));
        return isNaN(dt.getTime()) ? null : dt;
      };
      return { from: toDate(fromStr), to: toDate(toStr) };
    } catch {
      return { from: null, to: null };
    }
  };

  const renderCell = (key, res, dates) => {
    switch (key) {
      case "id":
        return <Text fontWeight="semibold">#{res.id}</Text>;
      case "customer":
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              width: 200,
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                flex: 1,
              }}
              title={res.customer_name || ""}
            >
              {res.customer_name || "-"}
            </span>
            {typeBadge(res)}
          </div>
        );
      case "from":
        return dates.from;
      case "to":
        return dates.to;
      case "vendor":
        return <Text>{res.vendor_name || "-"}</Text>;
      case "plate":
        return <Text>{res.car_plate || "-"}</Text>;
      case "model":
        return <Text>{res.car_model || "-"}</Text>;
      case "balance": {
        // Cancelled / no-show owe nothing, so their saldo is 0 regardless of
        // total_price (mirrors the recompute + RPC filter).
        const owed = ["cancelled", "no_show"].includes(res.status)
          ? 0
          : res.total_price || 0;
        const bal = Math.round((owed - (res.paid || 0)) * 100) / 100;
        if (Math.abs(bal) < 0.005) return "-";
        return (
          <span
            style={{
              color:
                bal <= 0
                  ? "var(--p-color-text-success)"
                  : "var(--p-color-text-critical)",
            }}
          >
            ${Math.abs(bal).toLocaleString()}
          </span>
        );
      }
      case "saldo_hoy": {
        // What the customer owes for the time consumed so far: rent accrued
        // from pickup through today (server-side compute_reservation_accrued_rent)
        // minus what's been paid. Floored at 0 — a customer who's paid ahead
        // owes nothing today; any credit shows in the full "Saldo" column.
        const owed = ["cancelled", "no_show"].includes(res.status)
          ? 0
          : res.accrued_to_today || 0;
        const bal = Math.max(
          0,
          Math.round((owed - (res.paid || 0)) * 100) / 100
        );
        if (bal < 0.005) return "-";
        return (
          <span style={{ color: "var(--p-color-text-critical)" }}>
            ${bal.toLocaleString()}
          </span>
        );
      }
      case "status":
        return (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {statusBadge(res)}
          </div>
        );
      case "invoice": {
        const invoiceable =
          res.type === "rental" &&
          res.total_price > 0 &&
          !["cancelled", "quotation", "no_show"].includes(res.status);
        if (!invoiceable) return null;
        const invoiced = res.amount_invoiced || 0;
        const uninvoiced = Math.round((res.total_price - invoiced) * 100) / 100;
        // Fully invoiced → nothing to show.
        if (uninvoiced < 0.005) return null;
        // No invoices at all → "Sin factura". Partially invoiced → show the
        // amount of the reservation still without an invoice.
        return (
          <Badge tone="warning" size="small">
            {invoiced < 0.005 ? "Sin factura" : `$${uninvoiced.toLocaleString()}`}
          </Badge>
        );
      }
      default:
        return null;
    }
  };

  const rows = reservations.map((res, index) => {
    const dates = parseDateRange(res.date_range);
    const url = `/reservation?id=${res.id}`;
    return (
      <IndexTable.Row
        id={res.id}
        key={res.id}
        position={index}
        onClick={(e) => {
          if (e?.metaKey || e?.ctrlKey || e?.button === 1) {
            window.open(url, "_blank");
          } else {
            navigate(url);
          }
        }}
      >
        {visibleColumns.map((key) => (
          <IndexTable.Cell key={key}>
            {key === "id" ? (
              <a
                href={url}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.metaKey || e.ctrlKey) {
                    window.open(url, "_blank");
                  } else {
                    navigate(url);
                  }
                }}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {renderCell(key, res, dates)}
              </a>
            ) : (
              renderCell(key, res, dates)
            )}
          </IndexTable.Cell>
        ))}
      </IndexTable.Row>
    );
  });

  // ---- Native Polaris advanced filters (multi-select, server-side) ----
  const setFilterAndReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };
  const labelsFor = (choices, vals) =>
    vals.map((v) => choices.find((c) => c.value === v)?.label || v).join(", ");

  const filters = [
    {
      key: "fecha",
      label: "Fecha",
      shortcut: true,
      filter: (
        <BlockStack gap="200">
          <TextField
            label="Desde"
            type="date"
            value={dateFrom}
            autoComplete="off"
            onChange={(v) => {
              setDateFrom(v);
              setPage(1);
            }}
          />
          <TextField
            label="Hasta"
            type="date"
            value={dateTo}
            autoComplete="off"
            onChange={(v) => {
              setDateTo(v);
              setPage(1);
            }}
          />
        </BlockStack>
      ),
    },
    {
      key: "vendor",
      label: "Vendedor",
      shortcut: true,
      filter: (
        <ChoiceList
          title="Vendedor"
          titleHidden
          allowMultiple
          choices={vendorChoices}
          selected={vendorFilter}
          onChange={setFilterAndReset(setVendorFilter)}
        />
      ),
    },
    {
      key: "balance",
      label: "Saldo",
      shortcut: true,
      filter: (
        <ChoiceList
          title="Saldo"
          titleHidden
          allowMultiple
          choices={BALANCE_CHOICES}
          selected={balanceFilter}
          onChange={setFilterAndReset(setBalanceFilter)}
        />
      ),
    },
    {
      key: "saldo_hoy",
      label: "Saldo a hoy",
      shortcut: true,
      filter: (
        <ChoiceList
          title="Saldo a hoy"
          titleHidden
          allowMultiple
          choices={SALDO_HOY_CHOICES}
          selected={saldoHoyFilter}
          onChange={setFilterAndReset(setSaldoHoyFilter)}
        />
      ),
    },
    {
      key: "status",
      label: "Estado",
      shortcut: true,
      filter: (
        <ChoiceList
          title="Estado"
          titleHidden
          allowMultiple
          choices={STATUS_CHOICES}
          selected={statusFilter}
          onChange={setFilterAndReset(setStatusFilter)}
        />
      ),
    },
    {
      key: "invoice",
      label: "Facturación",
      shortcut: true,
      filter: (
        <ChoiceList
          title="Facturación"
          titleHidden
          allowMultiple
          choices={INVOICE_CHOICES}
          selected={invoiceFilter}
          onChange={setFilterAndReset(setInvoiceFilter)}
        />
      ),
    },
  ];

  const appliedFilters = [];
  if (dateFrom || dateTo)
    appliedFilters.push({
      key: "fecha",
      label: `Fecha: ${dateFrom || "…"} – ${dateTo || "…"}`,
      onRemove: () => {
        setDateFrom("");
        setDateTo("");
        setPage(1);
      },
    });
  if (vendorFilter.length)
    appliedFilters.push({
      key: "vendor",
      label: `Vendedor: ${labelsFor(vendorChoices, vendorFilter)}`,
      onRemove: () => setFilterAndReset(setVendorFilter)([]),
    });
  if (balanceFilter.length)
    appliedFilters.push({
      key: "balance",
      label: `Saldo: ${labelsFor(BALANCE_CHOICES, balanceFilter)}`,
      onRemove: () => setFilterAndReset(setBalanceFilter)([]),
    });
  if (saldoHoyFilter.length)
    appliedFilters.push({
      key: "saldo_hoy",
      label: `Saldo a hoy: ${labelsFor(SALDO_HOY_CHOICES, saldoHoyFilter)}`,
      onRemove: () => setFilterAndReset(setSaldoHoyFilter)([]),
    });
  if (statusFilter.length)
    appliedFilters.push({
      key: "status",
      label: `Estado: ${labelsFor(STATUS_CHOICES, statusFilter)}`,
      onRemove: () => setFilterAndReset(setStatusFilter)([]),
    });
  if (invoiceFilter.length)
    appliedFilters.push({
      key: "invoice",
      label: `Facturación: ${labelsFor(INVOICE_CHOICES, invoiceFilter)}`,
      onRemove: () => setFilterAndReset(setInvoiceFilter)([]),
    });

  const clearAllFilters = () => {
    setVendorFilter([]);
    setBalanceFilter([]);
    setSaldoHoyFilter([]);
    setStatusFilter([]);
    setInvoiceFilter([]);
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <AppWrapper>
      <Page
        title="Reservas"
        primaryAction={
          <Button
            variant="primary"
            onClick={() => navigate("/reservations/create")}
          >
            Crear Reserva
          </Button>
        }
        secondaryActions={
          <Button
            loading={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                // Pull the full filtered set in one shot — the RPC is indexed
                // by branch + date_range, so even ~10k rows return fast.
                const { data, error } = await supabase.rpc("search_reservations", {
                  search_text: search?.trim() || null,
                  from_index: 0,
                  to_index: 100000,
                  date_from: dateFrom || null,
                  date_to: dateTo || null,
                  p_branch_id: branch?.id || null,
                  p_vendor: vendorFilter.length ? vendorFilter : null,
                  p_balance: balanceFilter.length ? balanceFilter : null,
                  p_status: statusFilter.length ? statusFilter : null,
                  p_invoice: invoiceFilter.length ? invoiceFilter : null,
                  p_saldo_hoy: saldoHoyFilter.length ? saldoHoyFilter : null,
                });
                if (error) {
                  alert("Error al exportar: " + error.message);
                  return;
                }
                const cleaned = (data || []).map((r) => {
                  const dates = parseDateRangeAsDates(r.date_range);
                  return {
                    ID: r.id,
                    Tipo: r.type === "service" ? "Servicio" : "Renta",
                    Cliente: r.customer_name || "",
                    Vendedor: r.vendor_name || "",
                    Placa: r.car_plate || "",
                    Modelo: r.car_model || "",
                    Entrega: toExcelSerial(dates.from) ?? "",
                    Devolución: toExcelSerial(dates.to) ?? "",
                    Total: r.total_price || 0,
                    Pagado: r.paid || 0,
                    Estado: statusLabel(r),
                    "Devengado a hoy": ["cancelled", "no_show"].includes(
                      r.status
                    )
                      ? 0
                      : r.accrued_to_today || 0,
                    "Saldo a hoy": Math.max(
                      0,
                      Math.round(
                        ((["cancelled", "no_show"].includes(r.status)
                          ? 0
                          : r.accrued_to_today || 0) -
                          (r.paid || 0)) *
                          100
                      ) / 100
                    ),
                  };
                });
                const ws = XLSX.utils.json_to_sheet(cleaned);
                // Dates get a date format (filterable) and every money column a
                // currency format on a real numeric cell, so the sheet can be
                // summed instead of just read.
                applyHeaderFormats(ws, {
                  Entrega: DATE_FMT,
                  Devolución: DATE_FMT,
                  ...moneyHeaders([
                    "Total",
                    "Pagado",
                    "Devengado a hoy",
                    "Saldo a hoy",
                  ]),
                });
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Reservas");
                const branchSlug = branch?.name
                  ? ` - ${branch.name}`
                  : "";
                XLSX.writeFile(wb, `Reservas${branchSlug} - GoMove.xlsx`);
              } finally {
                setExporting(false);
              }
            }}
          >
            Exportar Excel
          </Button>
        }
      >
        <BlockStack gap={400}>
          <LegacyCard>
            <div className="reservations-toolbar">
              <div className="reservations-filters">
                <Filters
                  queryValue={search}
                  queryPlaceholder="Buscar por cliente, placa o # de reserva"
                  onQueryChange={(v) => {
                    setSearch(v);
                    setPage(1);
                  }}
                  onQueryClear={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onClearAll={clearAllFilters}
                />
              </div>
              <div className="reservations-columnas">
                <Popover
                  active={columnsPopoverOpen}
                  activator={
                    <Button
                      icon={SettingsIcon}
                      onClick={() => setColumnsPopoverOpen((v) => !v)}
                    >
                      Columnas
                    </Button>
                  }
                  onClose={() => setColumnsPopoverOpen(false)}
                >
                  <div style={{ padding: "0.75rem 1rem", minWidth: 200 }}>
                    <ChoiceList
                      allowMultiple
                      title="Mostrar columnas"
                      choices={COLUMN_DEFS.map((c) => ({
                        label: c.required ? `${c.title} (requerida)` : c.title,
                        value: c.key,
                        disabled: c.required,
                      }))}
                      selected={visibleColumns}
                      onChange={handleColumnsChange}
                    />
                  </div>
                </Popover>
              </div>
            </div>
            {loading ? (
              <div
                style={{
                  padding: "2rem",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Spinner size="small" />
              </div>
            ) : (
              <>
                <IndexTable
                  resourceName={{
                    singular: "Reserva",
                    plural: "Reservas",
                  }}
                  selectable={false}
                  itemCount={reservations?.length ?? 0}
                  headings={visibleColumns.map((key) => ({
                    title:
                      COLUMN_DEFS.find((c) => c.key === key)?.title || "",
                  }))}
                >
                  {rows}
                </IndexTable>
                <div
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => setPage(page - 1)}
                    hasNext={hasMore}
                    onNext={() => setPage(page + 1)}
                  />
                </div>
              </>
            )}
          </LegacyCard>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Reservations;
